import React, { useCallback, useEffect, useMemo } from 'react';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';

import Box from '@mui/material/Box';
import type { ISnippet } from 'src/services/api/snippets/types';
import { useLoggedInUser } from 'src/services/api/auth/use-logged-in-user';
import { Flex, FlexColumn } from 'src/components/flex';
import { SnippetsIcon } from 'src/icons';
import Kbd from 'src/components/kbd/Kbd';
import { formatPlaybookMessageForFrontend } from 'src/common/playbook-variables/playbook-variable-utils';
import { useLeadMessagesPageContext } from 'src/pages/landlord/leads/messages/context/hooks';
import { useComposer } from 'src/pages/landlord/leads/messages/context/composer-hooks';
import { getMissingVariable, getSnippetSeededMessage } from 'src/pages/landlord/leads/messages/components/snippets-modal/utils';
import { PlaybookVariable } from 'src/services/api/playbooks/use-playbook-variables';

interface Props {
    snippets: ISnippet[];
    setSnippetsModalOpen: (snippetsModalOpen: boolean) => void;
}

export default function SnippetsModalList ({
    snippets,
    setSnippetsModalOpen,
}: Props) {
    const {
        lead,
        property,
        userSettings,
        search,
    } = useLeadMessagesPageContext();
    const { applySnippet } = useComposer();

    const { data: loggedInUser } = useLoggedInUser();

    const leadFirstName = lead.name?.split(' ')[0];
    const propertyAddress = property?.street_address;
    const propertyTourLink = property?.showing_scheduling_link?.link;
    const propertyApplicationLink = property?.application_link?.link;
    const rent = property?.rent ? property.rent.toString() : undefined;
    const numberOfBedrooms = property?.num_bedrooms ? property.num_bedrooms.toString() : undefined;
    const numberOfBathrooms = property?.num_bathrooms ? property.num_bathrooms.toString() : undefined;
    const emailSignature = userSettings?.signatures?.email?.text;
    const smsSignature = userSettings?.signatures?.sms?.text;

    const variableMap: Record<PlaybookVariable, string> = useMemo(() => ({
        [PlaybookVariable.LeadFirstName]: leadFirstName ?? '',
        [PlaybookVariable.PropertyAddress]: propertyAddress ?? '',
        [PlaybookVariable.PropertyTourLink]: propertyTourLink ?? '',
        [PlaybookVariable.PropertyApplicationLink]: propertyApplicationLink ?? '',
        [PlaybookVariable.Rent]: rent ?? '',
        [PlaybookVariable.NumberOfBedrooms]: numberOfBedrooms ?? '',
        [PlaybookVariable.NumberOfBathrooms]: numberOfBathrooms ?? '',
        [PlaybookVariable.SMSSignature]: smsSignature ?? '',
        [PlaybookVariable.EmailSignature]: emailSignature ?? '',
        [PlaybookVariable.YourName]: loggedInUser?.name ?? '',
        [PlaybookVariable.YourFirstName]: loggedInUser?.name?.split(' ')[0] ?? '',
    }), [leadFirstName, propertyAddress, propertyTourLink, propertyApplicationLink, rent, numberOfBedrooms, numberOfBathrooms, smsSignature, emailSignature, loggedInUser?.name]);

    const hasRequiredVariables = useCallback((snippetContents: string) => {
        return !getMissingVariable(
            snippetContents,
            variableMap,
        );
    }, [variableMap]);

    const handleClickSnippet = useCallback((snippet: ISnippet) => {
        applySnippet(snippet, getSnippetSeededMessage(snippet.contents, variableMap));
        setSnippetsModalOpen(false);
    }, [applySnippet, variableMap, setSnippetsModalOpen]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            const active = document.activeElement;
            if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) { return; }
            const index = parseInt(e.key) - 1;
            if (isNaN(index) || index < 0 || index > 8) { return; }
            const snippet = snippets[index];
            if (!snippet || !hasRequiredVariables(snippet.contents)) { return; }
            handleClickSnippet(snippet);
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [snippets, hasRequiredVariables, handleClickSnippet]);

    return (
        <FlexColumn>
            {snippets.length === 0 &&
                <Flex justifyContent="center">
                    <Typography sx={{ fontSize: '14px' }}>
                        {search ? 'No snippets match your search.' :'You do not have any snippets at the moment.'}
                    </Typography>
                </Flex>
            }

            {snippets.map((snippet, index) => {
                const snippetContents = snippet.contents;
                const isDisabled = !hasRequiredVariables(snippetContents);
                const missingVariable = getMissingVariable(
                    snippetContents,
                    variableMap,
                );
                const shortcutNumber = index < 9 ? index + 1 : null;

                return (
                    <React.Fragment key={snippet.id}>
                        <Tooltip
                            title={isDisabled ? `Missing info to use this snippet: ${missingVariable}` : ''}
                        >
                            <Flex
                                alignItems="flex-start"
                                onClick={() => !isDisabled && handleClickSnippet(snippet)}
                                data-testid="snippet-item"
                                data-snippet-id={snippet.id}
                                sx={{
                                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                                    padding: '10px 8px',
                                    mx: 2,
                                    borderRadius: 1,
                                    opacity: isDisabled ? 0.5 : 1,
                                    '&:hover': {
                                        backgroundColor: isDisabled ? 'transparent' : '#F7F7F4',
                                    },
                                }}
                            >
                                <Box sx={{ mt: '1px' }}><SnippetsIcon size="small" /></Box>
                                <FlexColumn gap={0} flex={1} sx={{ overflow: 'hidden' }}>
                                    <Typography
                                        className="ellipsisText"
                                        noWrap
                                        sx={{ fontSize: '14px', fontWeight: 600 }}
                                    >
                                        {snippet.name}
                                    </Typography>
                                    <Typography
                                        className="ellipsisText"
                                        noWrap
                                        sx={{ fontSize: '14px', fontWeight: 400 }}
                                    >
                                        {formatPlaybookMessageForFrontend(snippet.contents)}
                                    </Typography>
                                </FlexColumn>
                                {shortcutNumber != null && (
                                    <Kbd sx={{ ml: 1, alignSelf: 'center', flexShrink: 0, fontSize: '14px', display: { xs: 'none', sm: 'flex' } }}>
                                        {shortcutNumber}
                                    </Kbd>
                                )}
                            </Flex>
                        </Tooltip>
                        <Divider />
                    </React.Fragment>
                );
            })}
        </FlexColumn>
    );
}
